import { cellToBoundary, type CoordPair } from 'h3-js'
import { type MapRef } from 'react-map-gl'
import { useWindowSize } from 'usehooks-ts'
import { HIGHLIGHT_ZOOM } from '#app/consts'
import { useStoreContext } from '#app/store'

export type FlySheetType =
	| 'expand'
	| 'collapse'
	| 'current'
	| 'collapseThenExpand'

export const useFlyToPlot = () => {
	const windowSize = useWindowSize()
	const isSheetExpanded = useStoreContext(s => s.isSheetExpanded)
	const setIsSheetExpanded = useStoreContext(s => s.setIsSheetExpanded)

	const isMobile = windowSize.width < 768

	const padding = useStoreContext(s => s.viewState.padding)

	return (
		plot: string,
		mapRef: React.RefObject<MapRef>,
		sheetType: FlySheetType = 'current',
	) => {
		const center = cellToBoundary(plot, true)[0]

		if (isMobile && isSheetExpanded && sheetType === 'collapseThenExpand') {
			setIsSheetExpanded(false)
			setTimeout(() => {
				setIsSheetExpanded(true)
			}, 1000)
		}

		if (isMobile && sheetType === 'expand') {
			setIsSheetExpanded(true)
		}

		if (isMobile && sheetType === 'collapse') {
			setIsSheetExpanded(false)
		}

		mapRef.current?.flyTo({
			center,
			zoom: HIGHLIGHT_ZOOM,
			duration: 500,
			padding,
		})
	}
}

export const useFlyToGroupPlot = () => {
	const windowSize = useWindowSize()
	const loadedPlots = useStoreContext(s => s.loadedPlots)
	const setIsSheetExpanded = useStoreContext(s => s.setIsSheetExpanded)

	const isMobile = windowSize.width < 768

	const padding = useStoreContext(s => s.viewState.padding)

	return (
		groupId: string,
		mapRef: React.RefObject<MapRef>,
		sheetType: FlySheetType = 'current',
	) => {
		const plotsInGroup = loadedPlots.filter(plot => plot.groupId === groupId)
		const plotIds = plotsInGroup.map(plot => plot.plotId)

		const center = cellToBoundary(plotIds[0], true)[0]

		if (isMobile && sheetType === 'collapseThenExpand') {
			setIsSheetExpanded(false)
			setTimeout(() => {
				setIsSheetExpanded(true)
			}, 1000)
		}

		if (isMobile && sheetType === 'expand') {
			setIsSheetExpanded(true)
		}

		if (isMobile && sheetType === 'collapse') {
			setIsSheetExpanded(false)
		}

		mapRef.current?.flyTo({
			center,
			zoom: HIGHLIGHT_ZOOM,
			duration: 500,
			padding,
		})
	}
}

export const flyToLatLng = (
	latLng: CoordPair,
	mapRef: React.RefObject<MapRef>,
	mapPadding: {
		top: number
		bottom: number
		left: number
		right: number
	},
	zoom?: number,
) => {
	mapRef.current?.flyTo({
		center: latLng,
		zoom: zoom ?? HIGHLIGHT_ZOOM,
		padding: mapPadding,
		duration: 500,
	})
}
